import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { ClientResponse, ClientUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper, EMPTY_GUID } from '../utils';
import { ClientDetailsSectionHandler } from './details.section';
import { COMPANY_ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { ClientCustomizeSectionHandler } from './customize.section';
import { ClientSettingsSectionHandler } from './settings.section';
import { SharedAddressSectionHandler } from '../shared/address.section';
import { ClientMeasurementSectionHandler } from './measurement.section';
import { HttpClient } from '@angular/common/http';
import { SoftLimitSectionHandler } from '../shared/limits.section';
import { AppFeatures, APP_FEATURE_FLAGS, AppFeatureFlagDefaults } from 'app/app.features';
import { FeatureFlagsSectionHandler } from '../shared/app-features.section';
import { getBackendFeatureDefaults, BACKEND_FEATURE_FLAGS } from './backend.features';
import { nullAccessDenied } from 'app/services';
import { buildChartSectionGroup } from '../shared/chart.section';
import { ClientShiftSectionHandler } from './shift.section';
import { OidcIssuersSectionHandler } from '../shared/oidc-issuers.section';
import { ClientNotificationsSectionHandler } from './notifications.section';
import { RetentionSectionHandler } from '../shared/retention.section';
import { PasswordPolicySectionHandler } from '../shared/password-policy.section';

@RegisterEntityHandler('client')
@Injectable()
export class ClientHandler extends EntityHandler<ClientResponse> {

    constructor(
        admin: AdminEditorService,
        private httpClient: HttpClient
    ) {
        super(admin);
    }

    states = ['inactive', 'active', 'suspended', 'deleted'];

    counts = ['asset', 'device', 'companion-camera', 'user'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listClients/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.accounts.listClients(ownerId, offset, limit, sort, filter, this.counts.join(',')), 3).then(response => ({
            ...response,
            type: 'client',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.CLIENTS'),
        }));
    }

    getEntity(id: string): Promise<ClientResponse> {
        return this.admin.getCached(`getClient/${id}`, api => api.accounts.getClient(id), 10);
    }

    createEntity(ownerId: string, entity: any): Promise<ClientResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.accounts.createClient(entity), { _navigation: 'client' });
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, { state: state as any }), { _navigation: 'client' });
    }

    async getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {

        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.CLIENT.COLUMNS');

        const companyGroups = await this.admin.getCached(`listCompanyGroups/${ownerId}`, api => api.accounts.listCompanyGroups(ownerId)).catch(nullAccessDenied);

        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', COMPANY_ICONS.client),
            grid.TextColumn('website', 'WEBSITE'),
            companyGroups ? grid.ComboColumn('group', 'GROUP', [{ id: EMPTY_GUID, value: this.admin.i18n.instant('SHARED.NONE') }, ...companyGroups.items.map(x => ({ id: x.id, value: x.name }))], 80, true) : null,
            grid.StateColumn(this.states),
            ...this.counts.map(count => grid.NumberColumn(`counts.${count}`, count.replace(/-/g, '_').toUpperCase(), 60)),
            grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
        ].filter(x => x);

        return Promise.resolve(columns);
    }

    getEditorSections(ownerId: string, _entity: any): Promise<SectionHandlerGroup<ClientResponse>[]> {
        const backendFlags = this.admin.currentClient.flags[BACKEND_FEATURE_FLAGS];
        const sections: SectionHandlerGroup<ClientResponse>[] = [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new ClientDetailsSectionHandler(this.admin, ownerId, false),
                new ClientCustomizeSectionHandler(this.admin, ownerId, this.httpClient),
                new SharedAddressSectionHandler(this.admin, ownerId, 'client'),
            ],
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.SETTINGS'),
            sections: [
                new ClientSettingsSectionHandler(this.admin, ownerId),
                new ClientNotificationsSectionHandler(this.admin, ownerId),
                new ClientMeasurementSectionHandler(this.admin, ownerId),
                this.admin.user.owner.type === 'system' ? new PasswordPolicySectionHandler<ClientResponse>(this.admin, ownerId, 'client') : null, 
                this.admin.user.owner.type !== 'client' && new OidcIssuersSectionHandler<ClientResponse>(this.admin, ownerId, 'client'),
                backendFlags && backendFlags['shifts-shifts-enabled'] ? new ClientShiftSectionHandler(this.admin) : null, // shifts can only be set if enabled
                this.admin.user.owner.type === 'system' ? new SoftLimitSectionHandler<ClientResponse>(this.admin, ownerId, 'client') : null, // limits are only available to system users
                this.admin.user.owner.type === 'system' ? new RetentionSectionHandler<ClientResponse>(this.admin, ownerId, 'client') : null, 
            ].filter(x => x),
        }];

        if (this.admin.user.owner.type !== 'client') { // clients can't set their own features
            const features: SectionHandlerGroup<ClientResponse> = {
                name: this.admin.i18n.instant('ADMIN.EDITOR.FEATURES'),
                sections: [],
            };
            sections.push(features);

            features.sections.push(...Object.keys(AppFeatures).map(page => new FeatureFlagsSectionHandler<ClientResponse>(this.admin, ownerId, AppFeatures[page], AppFeatureFlagDefaults, APP_FEATURE_FLAGS, (entity, payload) => {
                return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload)).then(client => {
                    this.admin.clientUpdated(client);
                    return client;
                });
            })));

            const backend = getBackendFeatureDefaults(this.admin.user.owner.type);
            features.sections.push(...Object.keys(backend.sections).map(section => new FeatureFlagsSectionHandler<ClientResponse>(this.admin, ownerId, backend.sections[section], backend.defaults, backend.bucket,
                (ent, payload) => {
                    return this.admin.applyUpdate(api => api.accounts.updateClient(ent.id, payload), { _reload: true }).then(client => {
                        this.admin.clientUpdated(client);
                        return client;
                    });
                })));

        }

        sections.push(buildChartSectionGroup(this.admin, 'client', this.counts, ['7d', '30d', '60d', '90d']));

        return Promise.resolve(sections);
    }


    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: ClientUpdateRequest, sections: FormSectionHandler<ClientResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'client',
            entity: {}, // set up some defaults if you want
            sections: [
                new ClientDetailsSectionHandler(this.admin, ownerId, true),
            ],
        });
    }

}













